// src/context/persona/assist/ProposeStep.jsx — "Descrivi", step 1 of "Suggerisci personas" (v198; the first half
// of the old ProposePanel). The request text and the ticked documents are PAGE state (`value` / `onChange`), so
// closing the modal or going to the proposals and back keeps them. The call is synchronous (a few seconds) and
// SPENDS credits: refresh the meter after a 200. ⛔ NOT a form element: only the button calls the API; the
// textarea keeps Enter for new lines. ⛔ Every refusal is OUR sentence (billing → RunRefusalNotice, the rest →
// assistErrorCopy). The result goes up through `onResult` and is never persisted.
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DocumentPicker from '../../DocumentPicker.jsx';
import RunRefusalNotice from '../../../components/credits/RunRefusalNotice.jsx';
import { ModalFooter } from '../../../components/ui/WizardModal.jsx';
import { refusalFromError } from '../../../utils/runRefusal.js';
import { refreshEntitlements } from '../../../hooks/useEntitlements.js';
import { COLORS, SEMANTIC } from '../../../constants/theme.js';
import { proposePersonas } from '../../personaAssistApi.js';
import {
  alertBox, choiceHint, fieldLabel, fieldName, footerRow, ghostBtn, primaryBtn, sectionTextarea,
} from '../personaStyles.js';
import { PROPOSE_MAX, proposeProblem } from './assistModel.js';
import { assistErrorCopy } from './assistCopy.js';

const BUSY = { display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: COLORS.textSecondary };

export default function ProposeStep({ tenantId, value, onChange, onResult, onShowPrevious }) {
  const { t } = useTranslation('context');
  const [pickerBusy, setPickerBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const blocked = busy || pickerBusy || Boolean(proposeProblem(value.request));

  async function submit() {
    if (blocked) return;
    setBusy(true);
    setFailure(null);
    try {
      const res = await proposePersonas({ tenant: tenantId, request: value.request.trim(), documents: value.selected });
      refreshEntitlements();                // a propose SPENDS credits: refresh the meter
      onResult({
        proposals: Array.isArray(res?.proposals) ? res.proposals : [],
        kb_used: res?.kb_used,
        documents: Array.isArray(res?.documents) ? res.documents : [],
        at: Date.now(),                     // shown on "Proposte" ("generate alle 18:42") so an old list is recognisable
      });
    } catch (caught) {
      setFailure(caught || {});
    } finally {
      setBusy(false);
    }
  }

  const refusal = failure ? refusalFromError(failure) : null;
  return (
    <div data-testid="persona-propose-step">
      {/* "Indicazioni", as in Adatta: the label (+ a red "*", the step's only required field, kept OUTSIDE the
          <label> so the field's name stays exactly the label), a visible hint and an example placeholder. The
          request is Brian's STRONGEST input and the only Knowledge Base search query, so offer, customers, who
          decides and whom to grow with are what make the proposals useful. */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '6px' }}>
          <label htmlFor="persona-propose-request" style={{ ...fieldLabel, ...fieldName, marginBottom: 0 }}>
            {t('personas.assist.proposeLabel')}
          </label>
          <span aria-hidden="true" style={{ color: SEMANTIC.errore.fg, fontSize: '12px', fontWeight: 700 }}>*</span>
        </div>
        <p id="persona-propose-request-hint" style={{ ...choiceHint, marginTop: 0, marginBottom: '8px' }}>
          {t('personas.assist.proposeHint')}
        </p>
        <textarea
          id="persona-propose-request" aria-describedby="persona-propose-request-hint" aria-required="true"
          rows={5} value={value.request} maxLength={PROPOSE_MAX} disabled={busy}
          placeholder={t('personas.assist.proposePlaceholder')}
          onChange={(ev) => onChange({ ...value, request: ev.target.value })} style={sectionTextarea(busy)}
        />
      </div>
      {/* The documents section: the same title as the other two AI forms, with a hint fitted to this step — here
          Brian looks at the whole offer and customer base, not at one persona. */}
      <div style={{ marginBottom: '8px' }}>
        <h3 style={{ ...fieldName, margin: '0 0 6px' }}>{t('personas.assist.referenceDocsTitle')}</h3>
        <p style={{ ...choiceHint, marginTop: 0, marginBottom: '10px' }}>{t('personas.assist.proposeDocsHint')}</p>
      </div>
      <DocumentPicker
        tenantId={tenantId} label={t('personas.assist.docsLabel')} selected={value.selected}
        onChange={(selected) => onChange({ ...value, selected })} disabled={busy} onBusyChange={setPickerBusy}
      />
      {failure && (
        <div style={{ marginTop: '12px' }}>
          {refusal
            ? <RunRefusalNotice refusal={refusal} onDismiss={() => setFailure(null)} />
            : <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{assistErrorCopy(t, failure)}</div>}
        </div>
      )}
      <ModalFooter>
        <div style={footerRow}>
          {/* The call is SYNCHRONOUS (a few seconds, no job): the wait is shown HERE, in the fixed footer beside
              the button — at the bottom of the scrolling body, under the document list, it went unseen. */}
          {busy ? (
            <span role="status" style={BUSY}>
              <Loader2 size={16} aria-hidden="true" style={{ animation: 'persona-propose-spin 0.8s linear infinite' }} />
              <style>{'@keyframes persona-propose-spin { to { transform: rotate(360deg); } }'}</style>
              {t('personas.assist.proposeBusy')}
            </span>
          ) : null}
          <span style={{ flex: 1 }} />
          {/* Only when a previous list exists in this page: a new "Proponi" replaces it. */}
          {onShowPrevious && !busy && (
            <button type="button" onClick={onShowPrevious} style={ghostBtn(false)}>
              {t('personas.assist.proposePrevious')}
            </button>
          )}
          <button type="button" disabled={blocked} onClick={submit} style={primaryBtn(blocked)}>
            {t('personas.assist.proposeSubmit')}
          </button>
        </div>
      </ModalFooter>
    </div>
  );
}
